import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Typography,
  Paper,
  useTheme,
  alpha,
  IconButton,
  DialogActions,
  Tooltip
} from "@mui/material";
import { usePromptTemplates } from "../../hooks/usePromptTemplates";
import { TemplateDisplay } from "./TemplateDisplay";
import { useCallback, useEffect, useState } from "react";
import IPromptTemplate from "../../types/interfaces/IPromptTemplate";
import toTitleCase from "../../functions/formatting/toTitleCase";
import { useFormFactor } from "../../hooks/useFormFactor";
import { FormFactor } from "../../types/enums/FormFactor";
import { TemplatePreviewModal } from "./TemplatePreviewModal";
import CloseIcon from '@mui/icons-material/Close';
import AssignmentIcon from '@mui/icons-material/Assignment';
import CategoryIcon from '@mui/icons-material/Category';
import VisibilityIcon from '@mui/icons-material/Visibility';

export type TemplateSelectionModalProps = {
  open: boolean;
  onClose: () => void;
  onTemplateFilled: (newText: string) => void;
};

export const TemplateSelectionModal = ({
  open,
  onClose,
  onTemplateFilled,
}: TemplateSelectionModalProps) => {
  const { promptTemplates, templateRoles } = usePromptTemplates();
  const [templatePreviewOpen, setTemplatePreviewOpen] = useState(false);
  const formFactor = useFormFactor();
  const theme = useTheme();
  const [selectedRole, setSelectedRole] = useState<string | undefined>(
    undefined,
  );
  const [visibleTemplates, setVisibleTemplates] = useState<IPromptTemplate[]>(
    [],
  );
  const [selectedTemplate, setSelectedTemplate] = useState<
    IPromptTemplate | undefined
  >(undefined);
  const [savedText, setSavedText] = useState<string>("");

  const handleSelectRole = useCallback(
    (event: SelectChangeEvent) => {
      setSelectedRole(event.target.value);
      setSelectedTemplate(undefined);
    },
    [setSelectedRole, setSelectedTemplate],
  );

  const handleSelectTemplate = useCallback(
    (event: SelectChangeEvent) => {
      setSelectedTemplate(
        promptTemplates.find((t) => t.name === event.target.value),
      );
    },
    [promptTemplates, setSelectedTemplate],
  );

  const handleTextUpdated = useCallback(
    (newText: string) => {
      setSavedText(newText);
    },
    [selectedTemplate, setSavedText],
  );

  useEffect(() => {
    setVisibleTemplates(
      promptTemplates.filter(
        (t) => t.role.includes(selectedRole ?? "") || t.role.includes("*"),
      ),
    );
  }, [selectedRole, promptTemplates]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth={true}
      maxWidth="md"
      fullScreen={formFactor < FormFactor.Tablet}
      PaperProps={{
        sx: {
          borderRadius: 2,
          overflow: 'hidden',
          bgcolor: theme.palette.mode === 'dark' ? '#1e1e1e' : undefined
        }
      }}
    >
      <DialogTitle 
        sx={{ 
          backgroundColor: theme.palette.primary.main, 
          color: theme.palette.primary.contrastText,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          py: 1
        }}
      >
        <Typography variant="h6" component="div">
          Prompt Templates
        </Typography>
        <IconButton 
          edge="end" 
          color="inherit" 
          onClick={onClose} 
          aria-label="close"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ p: 3, bgcolor: theme.palette.mode === 'dark' ? '#1e1e1e' : undefined }}>
        <TemplatePreviewModal
          newText={savedText}
          open={templatePreviewOpen}
          onTemplateSaved={() => {
            onTemplateFilled(savedText);
          }}
          onPreviewClosed={() => {
            setTemplatePreviewOpen(false);
          }}
        />
        
        <Paper 
          elevation={0} 
          sx={{ 
            p: 2, 
            mb: 3, 
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            backgroundColor: theme.palette.mode === 'dark' ? '#1e1e1e' : alpha(theme.palette.background.paper, 0.7),
            borderRadius: 2
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
            <CategoryIcon 
              color="primary" 
              fontSize="small" 
              sx={{ mr: 1 }} 
            />
            <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
              Template Selection
            </Typography>
          </Box>
          
          <Stack spacing={3}>
            <FormControl fullWidth>
              <InputLabel id="template-role-select-label">Role</InputLabel>
              <Select
                labelId="template-role-select-label"
                id="template-role-select"
                value={selectedRole}
                label="Role"
                onChange={handleSelectRole}
                sx={{
                  bgcolor: theme.palette.mode === 'dark' ? '#1e1e1e' : undefined
                }}
              >
                {templateRoles.map((r) => (
                  <MenuItem value={r} key={r}>
                    {toTitleCase(r)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <FormControl fullWidth>
              <InputLabel id="template-select-label">Template</InputLabel>
              <Select
                labelId="template-select-label"
                id="template-select"
                value={selectedTemplate?.name}
                label="Template"
                onChange={handleSelectTemplate}
                sx={{
                  bgcolor: theme.palette.mode === 'dark' ? '#1e1e1e' : undefined
                }}
              >
                {visibleTemplates.map((t) => (
                  <MenuItem value={t.name} key={t.name}>
                    {toTitleCase(t.name)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </Paper>
        
        {selectedTemplate !== undefined ? (
          <Paper 
            elevation={0} 
            sx={{ 
              p: 2, 
              mb: 3, 
              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              backgroundColor: theme.palette.mode === 'dark' ? '#1e1e1e' : alpha(theme.palette.background.paper, 0.7),
              borderRadius: 2
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
              <AssignmentIcon 
                color="primary" 
                fontSize="small" 
                sx={{ mr: 1 }} 
              />
              <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                Template Fields
              </Typography>
            </Box>
            
            <Box sx={{ mt: 2 }}>
              <TemplateDisplay
                template={selectedTemplate}
                onTextUpdated={handleTextUpdated}
              />
            </Box>
          </Paper>
        ) : null}
      </DialogContent>
      
      <DialogActions sx={{ 
        px: 3, 
        py: 2,
        backgroundColor: theme.palette.mode === 'dark' ? '#1e1e1e' : alpha(theme.palette.background.paper, 0.5),
        borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`
      }}>
        <Button 
          onClick={onClose}
          variant="outlined"
          sx={{ 
            minWidth: '100px',
            borderRadius: 1.5
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={() => {
            setTemplatePreviewOpen(true);
          }}
          variant="outlined"
          startIcon={<VisibilityIcon />}
          sx={{ 
            minWidth: '100px',
            borderRadius: 1.5,
            ml: 1
          }}
        >
          Preview
        </Button>
        <Button
          onClick={() => {
            onTemplateFilled(savedText);
            onClose();
          }}
          variant="contained"
          color="primary"
          sx={{ 
            minWidth: '100px',
            borderRadius: 1.5,
            ml: 1
          }}
        >
          Insert
        </Button>
      </DialogActions>
    </Dialog>
  );
};
